import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * TenantContext — resolves the current Link Ride tenant (default or franchisee) and
 * exposes their branding so the customer app can show the operator's logo,
 * primary color, and support phone.
 *
 * Resolution priority (server-side; see routes/franchise.py::/tenant/current):
 *   1. ?tenant=<slug> URL query
 *   2. X-Tenant-Slug header
 *   3. Host subdomain (e.g. philly.linklitepro.com → "philly")
 *   4. Default Link Ride tenant
 *
 * Only one request per session. Applies `--brand-primary` CSS variable to the
 * document root so future components can read it via var(--brand-primary).
 */
const TenantContext = createContext(null);

const DEFAULT_TENANT = {
  slug: "linklitepro",
  display_name: "Link Ride",
  is_default: true,
  status: "active",
  branding: {
    logo_url: null,
    primary_color: "#0047FF",
    tagline: null,
    support_phone: null,
    tax_rate_pct: null,
  },
  city: null,
  state: null,
};

function readQueryTenant() {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search);
    return (p.get("tenant") || "").trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

// Install a single axios request interceptor that injects the current tenant
// slug on every request so the backend can pick per-tenant tax rate + branding
// even for endpoints that don't accept an explicit ?tenant= parameter.
let _tenantInterceptorInstalled = false;
function installTenantInterceptor() {
  if (_tenantInterceptorInstalled) return;
  _tenantInterceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    try {
      const slug =
        (typeof window !== "undefined" &&
          window.localStorage.getItem("link_tenant_slug")) ||
        readQueryTenant();
      if (slug && slug !== "linklitepro") {
        config.headers = config.headers || {};
        if (!config.headers["X-Tenant-Slug"]) {
          config.headers["X-Tenant-Slug"] = slug;
        }
      }
    } catch {
      /* ignore — never block a request over branding */
    }
    return config;
  });
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(DEFAULT_TENANT);
  const [loading, setLoading] = useState(true);

  // Install the axios interceptor once, at mount, before any other request
  // fires. Runs before the /tenant/current fetch below.
  useEffect(() => {
    installTenantInterceptor();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = readQueryTenant();
        const url = q ? `${API}/tenant/current?tenant=${encodeURIComponent(q)}` : `${API}/tenant/current`;
        const res = await axios.get(url, { timeout: 6000 });
        if (!cancelled && res.data) {
          const t = { ...DEFAULT_TENANT, ...res.data, branding: { ...DEFAULT_TENANT.branding, ...(res.data.branding || {}) } };
          setTenant(t);
          // Cache the slug so any part of the app can inject X-Tenant-Slug on
          // subsequent authenticated calls if the URL query is dropped.
          if (typeof window !== "undefined") {
            try { window.localStorage.setItem("link_tenant_slug", t.slug || "linklitepro"); } catch { /* ignore quota / private mode */ }
          }
        }
      } catch {
        // Silent fallback — default tenant already set
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply primary color as a CSS variable + document title once tenant loads
  useEffect(() => {
    if (typeof document === "undefined") return;
    const color = tenant?.branding?.primary_color || "#0047FF";
    document.documentElement.style.setProperty("--brand-primary", color);
    if (!tenant?.is_default && tenant?.display_name) {
      document.title = `${tenant.display_name} · powered by Link Ride`;
    }
  }, [tenant]);

  const value = useMemo(() => ({ tenant, loading }), [tenant, loading]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) return { tenant: DEFAULT_TENANT, loading: false };
  return ctx;
}
